import {
    Component,
    Input,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { FieldsetToggleComponent } from "@components/fieldset-toggle/fieldset-toggle.component";
import { TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import { chain } from "lodash";
import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IKeyValueGroup, ITplArbetsorder, ITplHandelssortiment } from "../../../models/dtos";
import { TplService } from "../../../services/tpl.service";
import { TplStateService } from "../../../state/tpl-state.service";
import { FilterBaseComponent } from "../filter-base-component";
import { TplFilterContext } from "../tpl-filter-context";
import { ITplHandelssortimentFilter } from "../tpl-filter.models";

@Component({
    selector: "rask-tpl-handelssortiment-filter",
    templateUrl: "./tpl-handelssortiment-filter.component.html",
    styleUrls: ["./tpl-handelssortiment-filter.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FieldsetToggleComponent,
        FormsModule,
        ReactiveFormsModule,
        TreeSelectorComponent
    ]
})
export class TplHandelssortimentFilterComponent extends FilterBaseComponent implements OnInit, ITplHandelssortimentFilter {
    @Input() dataSource: ITplArbetsorder[];
    items: SelectionItem[];

    private readonly tplService = inject(TplService);
    private readonly tplStateService = inject(TplStateService);

    constructor() {
        super("handelssortiment");
    }

    initHandelssortimentFilter(initialState?: number[]) {
        initialState = initialState ?? this.filterContext.filterState[this.name];

        if (!(this.dataSource?.length)) {
            return;
        }

        const hs = this.tplService.allHandelssortiment();
        this.items = this.initHandelssortimentOptions(hs);
        this.setSelectedHandelssortiment(initialState, this.items);
        this.selectionChanged();
    }

    isActive(): boolean {
        return this.value()?.length > 0;
    }

    match(item: ITplHandelssortiment): boolean {
        return this.value().indexOf(item.handelssortimentId) > -1;
    }

    ngOnInit(): void {
        this.onAddFilterToContext(this.filterContext);

        const initialState: number[] = this.filterContext.filterState[this.name] ?? [];
        this.open = initialState.length > 0;
        this.initHandelssortimentFilter(initialState);
    }

    selectionChanged() {
        const values = this.value();
        this.filterLength = values.length;
    }

    value(): number[] {
        return this.getSelectedHandelssortimentIdList(this.items);
    }

    protected onAddFilterToContext(filterContext: TplFilterContext) {
        filterContext.addHandelssortimentFilter(this.name, this);
    }

    protected onFilterInit(state: number[]): void {
        if (!state || !state.length) {
            this.clearSelectedHandelssortiment(this.items);
            return;
        }

        this.setSelectedHandelssortiment(state, this.items);
    }

    protected onFormChange(value: number[]): void {
        // empty
    }

    private readonly clearSelectedHandelssortiment = (items: SelectionItem[]) => {
        if (!ObjectUtils.hasValue(items)) {
            return;
        }

        items.forEach(x => {
            x.selected = false;
            x.partialSelected = false;
            if (x.children !== undefined) {
                this.clearSelectedHandelssortiment(x.children);
            }
        });

        this.selectionChanged();
        this.filterContext.filterState[this.name] = [];
    };

    private readonly getSelectedHandelssortimentIdList = (items: SelectionItem[]) => {
        if (!items) {
            return null;
        }

        const ids: number[] = [];

        items.forEach(grupp => {
            grupp.children
                .filter(hs => hs.selected)
                .forEach(hs => {
                    ids.push(hs.id);
                });
        });

        return ids;
    };

    private readonly initHandelssortimentOptions = (keys: IKeyValueGroup[]) => {
        // Temporär sorting regel. Bör använda sekvensnummer.
        const order =
            [
                "Talltimmer",
                "Grantimmer",
                "Lövtimmer",
                "Klentimmer barr",
                "Klentimmer tall",
                "Klentimmer gran",
                "Övrigt barrtimmer",
                "Barrmassaved",
                "Lövmassaved",
                "Energived",
                "Grot",
                "Träddelar",
                "Stubbar",
                "Övrigt"
            ];

        const isBransle = this.tplStateService.current.transportsatt.value.bransle;
        return chain(keys)
            .filter(x => this.tplService.handelssortimentMatchTransportsatt(x.group, isBransle))
            .orderBy(x => order.indexOf(x.group))
            .groupBy(x => x.group)
            .map((values, key) => ({
                text: key,
                children: values.map(v => ({
                    id: v.key,
                    text: v.value
                }))
            } as SelectionItem))
            .value();
    };

    private readonly setSelectedHandelssortiment = (filter: number[], items: SelectionItem[]) => {
        if (!ObjectUtils.hasValue(filter) || !ObjectUtils.hasValue(items)) {
            return;
        }

        items.forEach(hsGrupp => {
            let numberOfSelectedHs = 0;
            const nrOfHs = hsGrupp.children.length;
            hsGrupp.children.forEach(hs => {
                if (filter.includes(hs.id)) {
                    hs.selected = true;
                    numberOfSelectedHs++;
                }
            });
            if (numberOfSelectedHs > 0 && numberOfSelectedHs === nrOfHs) {
                hsGrupp.selected = true;
            } else if (numberOfSelectedHs > 0) {
                hsGrupp.partialSelected = true;
            }
        });
    };
}
